import { describe_error } from '../../utils/ErrorDescriber';
import React, { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';

import { is_valid_site_url, c_InvalidSiteUrlText } from '../../utils/SiteUrlValidation';

// Adds a site, or edits one when site is given. on_save(site_data) returns a promise and rejects with the message to
// show, '' when there is nothing to show. A site's url decides its pages, so it is only entered when the site is
// added. competitor_of_site, when given, is the main site a competitor is being added to: the modal asks for the
// competitor's name and url only, and site_data carries competitor_of_site_id.
function SiteModal({site, competitor_of_site, on_save, on_close}){
	const is_edit = site ? true : false;
	const is_competitor = competitor_of_site ? true : false;

	const [name, set_name] = useState(is_edit ? site.title : '');
	const [url, set_url] = useState(is_edit ? site.url : '');
	const [description, set_description] = useState((is_edit && site.description) ? site.description : '');
	const [saving, set_saving] = useState(false);
	const [error, set_error] = useState('');

	const handle_save = async(event)=>{
		event.preventDefault();

		if(name.trim().length == 0){
			set_error('Site name is required.');
			return;
		}

		if(!is_edit && (url.trim().length == 0)){
			set_error('Url is required.');
			return;
		}

		if(!is_edit && !is_valid_site_url(url)){
			set_error(c_InvalidSiteUrlText);
			return;
		}

		set_error('');
		set_saving(true);

		const site_data = {
			name: name.trim(),
			url: url.trim(),
			description: description.trim()
		};

		if(is_competitor){
			site_data.competitor_of_site_id = competitor_of_site.id;
		}

		try {
			await on_save(site_data);
		}
		catch (error) {
			console.log('SiteModal/handle_save - ERROR: ', describe_error(error));
			set_error(error.message);
			set_saving(false);
		}
	};

	const render_url_field = ()=>{
		if(is_edit){
			return (
				<Form.Group className="site-modal-field">
					<Form.Label>Url</Form.Label>
					<Form.Control
						type="text"
						value={url}
						readOnly={true}
						disabled={true}/>
				</Form.Group>
			);
		}

		return (
			<Form.Group className="site-modal-field">
				<Form.Label>Url</Form.Label>
				<Form.Control
					type="text"
					value={url}
					placeholder="https://example.com"
					disabled={saving}
					onChange={(event)=>{ set_url(event.target.value); }}/>
			</Form.Group>
		);
	};

	const render_save_label = ()=>{
		if(saving){
			return (
				<Spinner animation="border" size="sm"/>
			);
		}

		if(is_edit){
			return 'Save';
		}

		return is_competitor ? 'Add Competitor' : 'Add Site';
	};

	const get_title = ()=>{
		if(is_edit){
			return 'Edit Site';
		}

		return is_competitor ? 'Add Competitor' : 'Add Site';
	};

	const render_description_field = ()=>{
		if(is_competitor){
			return null;
		}

		return (
			<Form.Group className="site-modal-field">
				<Form.Label>Description</Form.Label>
				<Form.Control
					as="textarea"
					rows={3}
					value={description}
					placeholder="What the site is about"
					disabled={saving}
					onChange={(event)=>{ set_description(event.target.value); }}/>
			</Form.Group>
		);
	};

	const render_competitor_note = ()=>{
		if(!is_competitor){
			return null;
		}

		return (
			<p className="site-modal-note">
				{'A competitor of ' + competitor_of_site.title + '. It is crawled and analyzed like your own site, and uses your analysis credits.'}
			</p>
		);
	};

	return (
		<Modal show={true} onHide={on_close} centered>
			<Modal.Header closeButton>
				<Modal.Title>{get_title()}</Modal.Title>
			</Modal.Header>

			<Form onSubmit={handle_save}>
				<Modal.Body>
					{error && <Alert variant="danger">{error}</Alert>}

					{render_competitor_note()}

					<Form.Group className="site-modal-field">
						<Form.Label>Site name</Form.Label>
						<Form.Control
							type="text"
							value={name}
							placeholder="Site name"
							disabled={saving}
							onChange={(event)=>{ set_name(event.target.value); }}/>
					</Form.Group>

					{render_url_field()}

					{render_description_field()}
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" type="button" disabled={saving} onClick={on_close}>
						Cancel
					</Button>
					<Button variant="primary" type="submit" disabled={saving}>
						{render_save_label()}
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
}

export default SiteModal;
